import { StyleSheet } from 'react-native';
import React, { useState } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    SelectProps,
    Chip,
    Typography,
    FormLabel,
    FormControl,
    SxProps,
    Theme,
} from '@mui/material';
import chevron from '../../asset/images/icons/es_chevron_down.svg';
import { StyledBox } from '../Box/StylesBox';

export interface IDropdownSelect {
    label: string;
    data: string[];
    maxchipamount?: number;
    labelstyle?: React.CSSProperties;
    placeHolder?: string;
    onOptionSelected?: (value: string, index: number) => void;
    selectProps?: SelectProps;
    sx?: SxProps<Theme>;
}

const DropdownSelect: React.FC<IDropdownSelect> = props => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const { label, data, maxchipamount, labelstyle, placeHolder, selectProps, sx } = props;
    const [value, setValue] = useState<any>(placeHolder);
    const [multiValue, setMultiValue] = useState<any>([]);

    const handleChange = (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
        const {
            target: { value },
        } = event;
        if (selectProps && selectProps.multiple) {
            setMultiValue(typeof value === 'string' ? value.split(',') : value);
        } else {
            setValue(value);
        }
        const selectedIndex = (child as any)?.props?.['data-index'];
        props.onOptionSelected && props.onOptionSelected(value as string, selectedIndex);
    };

    const chipCount = maxchipamount ? maxchipamount : 3;
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                ...sx,
            }}>
            {label && <FormLabel style={{ ...styles.text, ...labelstyle }}>{label}</FormLabel>}
            <StyledBox
                style={styles.flexRow}
                sx={{
                    backgroundColor: theme[themeStyle].gray_text,
                    position: 'relative',
                    height: '50px',
                    paddingTop: '10px',
                    paddingRight: '14px',
                    paddingLeft: '14px',
                    paddingBottom: '10px',
                }}>
                <FormControl fullWidth>
                    <Select
                        variant="standard"
                        disableUnderline
                        inputProps={{ IconComponent: () => null }}
                        name={label}
                        value={selectProps && selectProps.multiple ? multiValue : value}
                        onChange={handleChange}
                        style={styles.datalist}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                        renderValue={(selected: any) =>
                            selectProps && selectProps.multiple ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.slice(0, maxchipamount).map((value: any) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            sx={{
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                fontSize: 8,
                                                color: '#fff',
                                                backgroundColor: 'rgb(54, 58, 61)',
                                                textTransform: 'uppercase',
                                                marginLeft: 0.5,
                                            }}
                                        />
                                    ))}
                                    {selected.length > chipCount && (
                                        <Chip
                                            key={value}
                                            label={`+${selected.length - chipCount}`}
                                            sx={{
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                fontSize: 8,
                                                color: '#fff',
                                                backgroundColor: 'rgb(54, 58, 61)',
                                                textTransform: 'uppercase',
                                                marginLeft: 0.5,
                                            }}
                                        />
                                    )}
                                </Box>
                            ) : (
                                <Typography style={styles.option}>{selected}</Typography>
                            )
                        }
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: theme[themeStyle].gray_text,
                                    '& .MuiMenuItem-root': {
                                        padding: 2,
                                    },
                                },
                            },
                        }}
                        {...selectProps}>
                        {data.map((option, index) => (
                            <MenuItem value={option} style={styles.option} key={index} data-index={index}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </StyledBox>
        </Box>
    );
};

DropdownSelect.defaultProps = {
    maxchipamount: 3,
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].form_label,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            borderRadius: 8,
        },
        option: {
            color: theme[colorScheme].lightWhite,
            fontSize: 16,
            cursor: 'pointer',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            fontWeight: '500',
            textTransform: 'capitalize',
        },
        absolute: {
            position: 'absolute',
            top: '0px',
            right: '0px',
            left: '0px',
            width: 'inherit',
            height: 'auto',
            zIndex: 100,
            backgroundColor: theme[colorScheme].gray_text,
        },
        datalist: {
            backgroundColor: theme[colorScheme].gray_text,
            height: '100%',
            width: '100%',
            border: 'none',
            borderRadius: 8,
            WebkitCalendarPickerIndicator: 'none',
            color: theme[colorScheme].lightWhite,
            backgroundImage: `url(${chevron})`,
            backgroundSize: '20px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right',
            position: 'relative',
        },
    });

export { DropdownSelect };
